
import { AudioPlayer, createAudioPlayer } from 'expo-audio';
import { captureException } from '../sentry/sentry';

/**
 * Handle returned by `preloadSound`. Exported so consumers do not have to reach
 * into the audio library's internals (the old code deep-imported
 * `expo-av/build/Audio/Sound`, which package `exports` maps now block).
 */
export type SoundHandle = AudioPlayer;

type SoundEffectId = keyof typeof soundEffects;

const soundEffects = {
  lauraBreatheIn: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/laurainhale.mp3' },
  lauraBreatheOut: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/lauraexhale.mp3' },
  lauraHold: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/laurahold.mp3' },
  paulBreatheIn: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/paulinhale.mp3' },
  paulBreatheOut: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/paulexhale.mp3' },
  paulHold: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/paulhold.mp3' },
  bellBreatheIn: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/cuebell1.mp3' },
  bellBreatheOut: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/cuebell1.mp3' },
  bellHold: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/cuebell2.mp3' },
  endingBell: { path: 'https://s3.eu-west-1.amazonaws.com/evoluno.app.files/breathingAudio/endingbell1.mp3' },
};

const PRELOAD_TIMEOUT_MS = 5000;

export const preloadSound = async(path: string) : Promise<SoundHandle | undefined> => {
  try {
    // createAudioPlayer is expo-audio's imperative constructor - the hook form
    // (useAudioPlayer) cannot be used here because these helpers are called from
    // effects and callbacks rather than at render time.
    //
    // Unlike expo-av's loadAsync, createAudioPlayer returns immediately and loads
    // in the background, so "preload" must wait for readiness itself: without
    // this, the first breathing cue on a cold network was a silent no-op (play()
    // on a not-yet-loaded player). Bounded so a dead URL degrades to the old
    // fire-and-hope behaviour instead of hanging the exercise forever.
    const player = createAudioPlayer({ uri: path });
    await new Promise<void>(resolve => {
      if (player.isLoaded) { resolve(); return; }
      const subscription = player.addListener('playbackStatusUpdate', status => {
        if (status.isLoaded) {
          subscription.remove();
          resolve();
        }
      });
      setTimeout(() => {
        subscription.remove();
        if (!player.isLoaded) {
          captureException(new Error(`preloadSound: still not loaded after ${PRELOAD_TIMEOUT_MS}ms: ${path}`));
        }
        resolve();
      }, PRELOAD_TIMEOUT_MS);
    });
    return player;
  } catch (e) {
    captureException(e);
  }
};

export const playSound = async (sound: SoundHandle | undefined) => {
  if (sound) {
    try {
      // expo-av's playFromPositionAsync(0) becomes seek-then-play
      await sound.seekTo(0);
      sound.play();
    } catch (e) {
      captureException(e);
    }
  }
};

export const unload = async (sound: SoundHandle | undefined) => {
  if (sound) {
    try {
      if (sound.isLoaded) {
        sound.pause();
      }
      // AudioPlayer is a SharedObject; remove() releases the native instance
      // (expo-av's unloadAsync equivalent).
      sound.remove();
    } catch (error) {
      captureException(error);
    }
  }
};